import { MagnifyingGlass, XCircle } from '@phosphor-icons/react';
import { format as formatDate } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { CallDetailsModal } from 'src/components/CallDetailsModal';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Info } from 'src/components/Info';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { Option, SelectBox } from 'src/components/SelectBox';
import {
  SideDropAction,
  SideDropActions,
} from 'src/components/SideDropActions';
import Table, { Header, Row } from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import { Call } from 'src/interfaces/call.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import {
  FluentsActionKey,
  PaginatedResponse,
  WorkflowActionProvider,
  WorkflowExecutionCancelationReason,
  WorkflowExecutionStatus,
  WorkflowExecutionThreadActionHistoryRecord,
  WorkflowExecutionThreadFindAllFilters,
  WorkflowExecutionThreadStatus,
  WorkflowExecutionThreadWithComputedStatus,
} from 'src/interfaces/workflows.interface';
import {
  ActivityItemData,
  ActivityList,
} from 'src/pages/Automations/components/ui/ActivityList';
import { friendlyDateFormatter } from 'src/utils/date';
import { extractDigits, processPhoneNumber } from 'src/utils/number';

const TO_NUMBER_PARAM_KEY = 'to_number';

const ALL_THREAD_STATUSES_OPTION: Option = {
  label: 'All Statuses',
  value: '',
};

const THREAD_STATUS_OPTIONS: Option[] = [
  ALL_THREAD_STATUSES_OPTION,
  { label: 'Scheduled', value: WorkflowExecutionThreadStatus.RUNNING },
  { label: 'Queued', value: WorkflowExecutionThreadStatus.RATE_LIMITED },
  { label: 'Inactive', value: WorkflowExecutionThreadStatus.PAUSED },
  { label: 'Canceled', value: WorkflowExecutionThreadStatus.CANCELED },
  { label: 'Errored', value: WorkflowExecutionThreadStatus.ERRORED },
  { label: 'Completed', value: WorkflowExecutionThreadStatus.COMPLETED },
];

type ActivityFilters = {
  status: string;
  to_number: string;
};

const getStatusDisplay = (
  thread: WorkflowExecutionThreadWithComputedStatus,
): string => {
  if (thread.status === WorkflowExecutionThreadStatus.CANCELED) {
    if (
      thread.execution_status === WorkflowExecutionStatus.EXECUTION_CANCELED &&
      (thread.execution_cancelation_reason ===
        WorkflowExecutionCancelationReason.REASON_CALL_ANSWERED ||
        thread.execution_cancelation_reason ===
          WorkflowExecutionCancelationReason.REASON_INCOMING_CALL)
    ) {
      return 'Stopped Follow-ups — Contact Reached';
    }
    return 'Canceled';
  }

  if (thread.status === WorkflowExecutionThreadStatus.ERRORED) {
    return 'Errored';
  }

  if (thread.status === WorkflowExecutionThreadStatus.PAUSED) {
    return 'Inactive';
  }

  if (
    thread.status === WorkflowExecutionThreadStatus.RUNNING ||
    thread.status === WorkflowExecutionThreadStatus.RATE_LIMITED
  ) {
    let status = 'Scheduled';
    if (thread.status === WorkflowExecutionThreadStatus.RATE_LIMITED) {
      return 'Queued — Max Concurrent Calls';
    }

    if (thread.resume_at) {
      if (
        thread.pending_action &&
        !thread.pending_action.result &&
        !thread.pending_action.error
      ) {
        if (
          thread.pending_action?.action_provider ===
            WorkflowActionProvider.FLUENTS &&
          thread.pending_action?.action_config?.action_key ===
            FluentsActionKey.CREATE_CALL
        ) {
          return 'Calling';
        }
        return 'Outreach In Progress';
      } else {
        const executionNextResumeAtDate = new Date(thread.resume_at);
        if (executionNextResumeAtDate > new Date()) {
          const nextResumeAtStr = formatDate(
            executionNextResumeAtDate,
            'MM/dd/yyyy HH:mm:ss',
          );
          status += ` for ${nextResumeAtStr}`;
        }
      }
    }
    return status;
  }

  return 'Completed';
};

const getActivityItems = (
  actionHistory: WorkflowExecutionThreadActionHistoryRecord[],
  onActivityClick: (
    history: WorkflowExecutionThreadActionHistoryRecord,
  ) => void,
): ActivityItemData[] => {
  return actionHistory
    .map((history, i) => {
      let label = '';
      let icon = undefined;
      let onClick = undefined;

      switch (history.action_config.action_key) {
        case 'create_call':
          label = i === 0 ? 'Initial Call' : 'Follow-up Call';
          icon = MagnifyingGlass;
          onClick = () => onActivityClick(history);
          break;
        case 'send_email':
          label = 'Follow-up Email';
          break;
        case 'send_message':
          label = 'Follow-up SMS';
          break;
        default:
          return null;
      }

      return {
        completedAt: history.completed_at || '',
        label,
        icon,
        onClick,
      } as ActivityItemData;
    })
    .filter((item): item is ActivityItemData => item !== null);
};

export const ActivityContent = () => {
  const { id: campaignReferenceId } = useParams();
  const { role } = useEnvironment();
  const { get, post } = useAuthedRequest();
  const notification = useNotification();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [selectedThread, setSelectedThread] =
    useState<WorkflowExecutionThreadWithComputedStatus | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);

  const [filters, setFilters] = useState<ActivityFilters>({
    status: ALL_THREAD_STATUSES_OPTION.value,
    to_number: '',
  });

  const headers: Header[] = [
    { key: 'id', label: 'ID', width: '8rem', disableSorting: true },
    { key: 'updated_at', label: 'Updated At', width: '10%' },
    { key: 'toNumber', label: 'To', width: '10%', disableSorting: true },
    { key: 'status', label: 'Status', width: '15%', disableSorting: true },
    { key: 'activity', label: 'Activity', width: '30%', disableSorting: true },
    {
      key: 'action',
      label: '',
      width: '10%',
      disableSorting: true,
      align: 'center',
    },
  ];

  const updateFilters = (newFilters: Partial<ActivityFilters>) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
    setCurrentPage(1);
  };

  const getCallDetails = useCallback(async (callId: string) => {
    const call = await get(`/calls/${callId}`);
    const agent = call.agent as unknown as {
      name: string;
      id: string;
    };
    call.agent =
      agent.name && agent.id ? `${agent.name} (${agent.id})` : agent.id;
    return call;
  }, []);

  const selectActionHistory = useCallback(
    async (actionHistory: WorkflowExecutionThreadActionHistoryRecord) => {
      if (
        actionHistory.action_provider !== WorkflowActionProvider.FLUENTS ||
        actionHistory.action_config.action_key !== FluentsActionKey.CREATE_CALL
      ) {
        return;
      }

      if (!actionHistory.result || !actionHistory.result.id) return;

      setLoading(true);
      try {
        const call = await getCallDetails(actionHistory.result.id as string);
        setSelectedCall(call);
      } catch (error) {
        console.error('Error fetching call details:', error);
        notification.error('Failed to load call details');
      } finally {
        setLoading(false);
      }
    },
    [getCallDetails],
  );

  const fetchThreads = useCallback(
    async (page: number, pageSize: number) => {
      if (!campaignReferenceId) return;

      setLoading(true);
      try {
        const queryFilters: WorkflowExecutionThreadFindAllFilters = {
          workflowReferenceId: campaignReferenceId,
        };

        if (filters.status !== ALL_THREAD_STATUSES_OPTION.value) {
          queryFilters.statuses = [
            filters.status as WorkflowExecutionThreadStatus,
          ];
        }

        const numberDigitsOnly = extractDigits(filters.to_number || '');
        if (numberDigitsOnly.length > 0) {
          queryFilters.executionParams = {
            [TO_NUMBER_PARAM_KEY]: numberDigitsOnly,
          };
        }

        const queryObject: QueryObject = {
          page: String(page),
          size: String(pageSize),
          filters: JSON.stringify(queryFilters),
        };

        if (sortConfig.key && sortConfig.direction) {
          queryObject.sort_column = sortConfig.key;
          queryObject.sort_desc =
            sortConfig.direction === 'desc' ? 'true' : 'false';
        }

        const query = new URLSearchParams(
          queryObject as Record<string, string>,
        );

        const data: PaginatedResponse<WorkflowExecutionThreadWithComputedStatus> =
          await get(`/workflow_threads?${query.toString()}`);

        const items = data.items.map(
          (thread: WorkflowExecutionThreadWithComputedStatus) => {
            const statusDisplay = getStatusDisplay(thread);
            const cancelable =
              thread.status === WorkflowExecutionThreadStatus.PAUSED ||
              thread.status === WorkflowExecutionThreadStatus.RUNNING ||
              thread.status === WorkflowExecutionThreadStatus.RATE_LIMITED;

            const lastActionHistoryRecord =
              thread.action_history.length > 0
                ? thread.action_history[thread.action_history.length - 1]
                : undefined;
            const lastErrorMessage = lastActionHistoryRecord?.error?.trim();

            const statusWithError =
              thread.status === WorkflowExecutionThreadStatus.ERRORED &&
              lastErrorMessage ? (
                <div className="flex items-center gap-2">
                  <span>Errored</span>
                  <Info value={lastErrorMessage} />
                </div>
              ) : (
                statusDisplay
              );

            const actions: SideDropAction[] = [
              {
                label: 'Cancel',
                Icon: XCircle,
                onClick: () => {
                  setSelectedThread(thread);
                  setCancelModalOpen(true);
                },
                disabled: !cancelable,
                show: can(role, 'update', Subject.CAMPAIGNS),
              },
            ];

            const activityItems = getActivityItems(
              thread.action_history,
              selectActionHistory,
            );
            const activity =
              activityItems.length > 0 ? (
                <ActivityList items={activityItems} />
              ) : (
                '—'
              );

            return {
              id: (
                <CopyableField value={thread.id} notification={notification} />
              ),
              updated_at: friendlyDateFormatter(new Date(thread.updated_at)),
              toNumber: processPhoneNumber(
                (thread.execution_params?.to_number as string) || '',
              ),
              status: statusWithError,
              activity,
              action: (
                <div
                  className="flex justify-center items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <SideDropActions options={actions} />
                </div>
              ),
            };
          },
        );

        setRows(items);
        setTotalItems(data.total);
      } catch (error) {
        console.error('Error fetching threads:', error);
        notification.error('Failed to load activity');
      } finally {
        setLoading(false);
      }
    },
    [
      campaignReferenceId,
      filters.status,
      filters.to_number,
      sortConfig.key,
      sortConfig.direction,
      role,
      selectActionHistory,
    ],
  );

  useEffect(() => {
    if (campaignReferenceId) {
      fetchThreads(currentPage, currentPageSize);
    }
  }, [
    campaignReferenceId,
    currentPage,
    currentPageSize,
    sortConfig.key,
    sortConfig.direction,
    filters.status,
    filters.to_number,
    fetchThreads,
  ]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setCurrentPageSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setCurrentPageSize(perPage);
    setCurrentPage(1);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const handleCancelCall = async () => {
    if (!selectedThread) return;
    try {
      await post(
        `/workflow_executions/${selectedThread.execution_id}/cancel`,
        {},
      );
      notification.success('Successfully canceled scheduled call');
      fetchThreads(currentPage, currentPageSize);
      setCancelModalOpen(false);
      setSelectedThread(null);
    } catch (error) {
      console.error('Error canceling scheduled call:', error);
      notification.error('Failed to cancel scheduled call');
    }
  };

  const renderSkeletonRows = (): Row[] => {
    return Array.from({ length: currentPageSize }).map(() => ({
      id: <Skeleton height={20} width={100} />,
      updated_at: <Skeleton height={20} width={120} />,
      toNumber: <Skeleton height={20} width={100} />,
      status: <Skeleton height={20} width={120} />,
      activity: <Skeleton height={20} width={200} />,
      action: <Skeleton height={20} width={40} />,
    }));
  };

  if (!campaignReferenceId) {
    return null;
  }

  return (
    <ContentWrapper>
      <Can operation="read" subject={Subject.CAMPAIGNS}>
        <div className="mb-6">
          <div className="flex flex-col gap-4 sm:gap-2 sm:grid-cols-2 md:grid md:grid-cols-2 lg:grid-cols-2 mt-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <SelectBox
                options={THREAD_STATUS_OPTIONS}
                defaultValue={THREAD_STATUS_OPTIONS.find(
                  (option) => option.value === filters.status,
                )}
                variant="outlined"
                color="primary"
                size="medium"
                onChange={(value) => updateFilters({ status: value })}
              />
            </div>

            <div>
              <Input
                label="To Number"
                type="text"
                value={filters.to_number}
                onChange={(e) => updateFilters({ to_number: e.target.value })}
              />
            </div>
          </div>
        </div>

        <Table
          headers={headers}
          rows={loading && rows.length === 0 ? renderSkeletonRows() : rows}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSort={handleSort}
          loading={loading}
          setSize={handleSizeChange}
          defaultSize={currentPageSize}
        />
      </Can>

      <Modal
        title="Cancel Scheduled Call"
        isOpen={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setSelectedThread(null);
        }}
        actionButton={
          <Button onClick={handleCancelCall}>Cancel Scheduled Call</Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to cancel this scheduled call?
        </div>
      </Modal>

      {selectedCall && (
        <CallDetailsModal
          call={selectedCall}
          isOpen={!!selectedCall}
          onClose={() => {
            setSelectedCall(null);
          }}
        />
      )}
    </ContentWrapper>
  );
};
